// Third party imports ( in /node_modules )
// Handles page routing
import { Location, NavigateFn, Router } from '@reach/router';
// Take javascript in .ts and .tsx files and turns it into html
import * as React from 'react';
// Take javascript in .ts and .tsx files and turns it into css
import styled from 'react-emotion';
// State inn /src/store
import { connect } from 'react-redux';

import { Businesses, Theme } from '@just-in-types';
// Only one default export per file ( {no curly brackets} /Users/joshuasprow/Projects/just-inventory-north/kiosk/src/Business.tsx );
import Business from './Business';
import Camera from './components/Camera';
import NewVersionButton from './components/NewVersionButton';
import ErrorBoundary from './hoc/withErrorBoundary';
import Site, { RouteComponent } from './Site';
import {
  getAppInfo,
  getCredentials,
  getTabletProps,
  IKioskCredentials,
  setRoutes,
  SetRoutes,
} from './store/actions';
import { IState } from './store/reducers';
import Home from './views/Home';

export interface AppDispatchProps {
  onSetRoutes: SetRoutes;
}

export interface AppStateProps {
  businesses: Businesses.Business[];
  credentials: null | IKioskCredentials;
  navigate: NavigateFn;
  theme: Theme.Props;
}

type AppProps = AppDispatchProps & AppStateProps;

interface IAppContainerProps {
  background: string;
}

const AppContainer = styled('div')`
  position: relative;
  min-width: 100vw;
  max-width: 100vw;
  min-height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  user-select: none;
  ::before {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    width: 100%;
    height: 100%;
    background: ${(props: IAppContainerProps) => props.background};
    background-size: cover;
    background-position: center;
    filter: grayscale(0.5);
    z-index: -1;
  }
  .fade-enter,
  .fade-enter > div {
    opacity: 0;
  }

  .fade-enter.fade-enter-active,
  .fade-enter.fade-enter-active > div {
    opacity: 1;
    transition: opacity 500ms ease-in-out;
  }
`;

class App extends React.Component<AppProps> {
  public componentDidMount() {
    this.props.onSetRoutes();

    getAppInfo();
    getTabletProps();
  }

  public render() {
    const { businesses, theme } = this.props;

    return (
      <ErrorBoundary>
        <AppContainer background={theme.background}>
          <Location>
            {({ location, navigate }) => (
              <Router location={location}>
                <Home path="/" routes={businesses} />
                {businesses.map((business, businessIndex) => (
                  <Business
                    business={business}
                    key={businessIndex}
                    path={business.path}>
                    <Home default={true} routes={business.sites} />
                    {business.sites.map((site, siteIndex) => (
                      <Site
                        business={business}
                        site={site}
                        key={siteIndex}
                        path={site.path}>
                        {(components: RouteComponent[]) => (
                          <Router>
                            {components.map(
                              (route: RouteComponent, index) =>
                                route.component && (
                                  <route.component
                                    businessId={business.businessId}
                                    default={route.default}
                                    key={index}
                                    site={site}
                                    siteId={site.siteId}
                                    navigate={navigate}
                                    path={route.path}
                                    routes={site.routes}
                                    takeMugshot={route.takeMugshot}
                                    waiverText={route.waiverText}
                                  />
                                ),
                            )}
                          </Router>
                        )}
                      </Site>
                    ))}
                  </Business>
                ))}
              </Router>
            )}
          </Location>
          <Camera />
          <NewVersionButton />
        </AppContainer>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = ({
  router: { businesses, navigate },
  telemetry: { credentials },
  theme,
}: IState) => ({
  businesses,
  credentials,
  navigate,
  theme,
});

const mapDispatchToProps = {
  onSetRoutes: setRoutes,
};

export default connect<AppStateProps, AppDispatchProps>(
  // @ts-ignore
  mapStateToProps,
  mapDispatchToProps,
)(App);



// WEBPACK FOOTER //
// ./src/App.tsx